import React, {useEffect, useState, useRef} from 'react';

import {i18n as i18nType} from 'i18next';
import {useTranslation} from 'react-i18next';
import {AnySchema, ValidationError} from 'yup';

import {ReactComponent as DownloadIcon} from 'assets/svg/download2.svg';
import {getTemplate} from 'components/pages/Registries/mock';
import {Icon} from 'components/ui-kit/Icon';
import {colors} from 'theme';
import {readXlsx} from 'utils/readXlsx';

import {ErrorList, ErrorItem, Root, CustomLink, CustomFileInput, CustomLinkAction} from './styled';

interface MapObject {
    [key: string]: string;
}

export interface FileFormPropsCallback {
    file?: File | null;
    data: Array<MapObject>;
    errors?: Array<string>;
    isValid?: boolean;
}
export interface FileFormProps {
    onChange(data: FileFormPropsCallback): void;
    scheme?: AnySchema;
    assessors?: MapObject;
    localizedFields?: MapObject;
    initialValue?: File | null;
    linkExample?: string;
    linkExampleAction?: (link: string) => void;
    errors?: XLSXError[];
    serverValidate?: boolean;
    horizontal?: boolean;
    title?: string;
    updatedDesign?: boolean;
}

export type XLSXError = {field: string; error: string};

enum GetDataByLanguageKeys {
    translation = 'translation',
    dictionaries = 'dictionaries',
}

const getNestedValue = (obj: any, path: string): any =>
    path.split('.').reduce((current, key) => current?.[key], obj);

const normalizeLocalizedField = (
    fieldName: string,
    item: string,
    i18n: i18nType,
    localizedFields?: MapObject,
) => {
    const dictionaryPath = localizedFields?.[fieldName];

    if (!dictionaryPath || !item) return item;

    const dictionaries = i18n.getDataByLanguage(i18n.language)?.[
        GetDataByLanguageKeys.translation
    ]?.[GetDataByLanguageKeys.dictionaries] as Record<string, string> | undefined;

    if (!dictionaries) return item;

    const dictionary = getNestedValue(dictionaries, dictionaryPath);

    if (dictionary && typeof dictionary === 'object') {
        const foundItem = Object.entries(dictionary).find(
            ([, value]) => (value as string).toLowerCase() === item.toLowerCase(),
        );

        return foundItem ? foundItem[0] : item;
    }

    return item;
};

export const mapByAssessors = (
    array: Array<MapObject>,
    assessors: MapObject,
    i18n: i18nType,
    localizedFields?: MapObject,
) =>
    array.reduce((acc: Array<MapObject>, item: MapObject) => {
        const assessorKeys = Object.keys(assessors);
        const hasMappedValues = assessorKeys.some((name: string) => {
            const value = item[assessors[name]];

            return value !== undefined && value !== null && String(value).trim() !== '';
        });

        if (!hasMappedValues) {
            return acc;
        }

        const result: MapObject = {};
        assessorKeys.forEach((name: string) => {
            result[name] = normalizeLocalizedField(
                name,
                item[assessors[name]],
                i18n,
                localizedFields,
            );
        });

        acc.push(result);
        return acc;
    }, []);

export const mapByTemplate = (array: string[], file_type: string) => {
    const template = getTemplate(file_type);
    const templateValues = Object.values(template);
    console.log(templateValues);
    console.log(array);
    return array.reduce((acc, item, index) => {
        Object.assign(acc, {[templateValues[index]]: item});
        return acc;
    }, {});
};

export const XlsxFileForm = ({
    onChange,
    scheme,
    initialValue,
    assessors,
    localizedFields,
    linkExample,
    linkExampleAction,
    errors: errorsList,
    serverValidate,
    horizontal,
    title,
    updatedDesign,
}: FileFormProps) => {
    const {t, i18n} = useTranslation();
    const didMountRef = useRef(false);
    const [file, fileChanged] = useState<File | null>(initialValue || null);
    const [errors, errorsChanged] = useState<Array<string> | null>(null);
    const [data, dataChanged] = useState<Array<MapObject>>([]);

    const handleChangeFile = (evt: any): void => {
        const {file} = readXlsx(evt, (data: any, _: File, file_type: string) => {
            const mappedData = mapByTemplate(data, file_type);
            console.log(mappedData);
            dataChanged(assessors ? mapByAssessors(data, assessors, i18n, localizedFields) : data);
        });

        if (serverValidate) return fileChanged(file);

        if (file) {
            fileChanged(file);
        } else {
            fileChanged(null);
            dataChanged([]);
        }
    };

    useEffect(() => {
        if (!serverValidate && scheme && file && didMountRef.current) {
            scheme
                .validate(data, {abortEarly: false})
                .then(() => {
                    errorsChanged([]);
                })
                .catch((err) => {
                    const errors: Array<string> = [];
                    err.inner.forEach((e: ValidationError) => {
                        const path = e.path ? e.path.match(/\[(.*)\]/) ?? [] : [];
                        const stringCount = path.pop();
                        errors.push(
                            stringCount
                                ? `${t('components.xlsxForm.row')}: ${
                                      stringCount ? parseInt(stringCount, 10) + 2 : '-'
                                  }: ` + (i18n.exists(e.message) ? t(e.message) : e.message)
                                : e.message,
                        );
                    });
                    console.log(errors);
                    errorsChanged(errors);
                });
        } else if (didMountRef.current && !file) {
            errorsChanged(null);
        }
    }, [data]);

    useEffect(() => {
        if (!file && didMountRef.current) {
            dataChanged([]);
        }
    }, [file]);

    useEffect(() => {
        if (didMountRef.current) {
            onChange({
                file,
                data,
                errors: errors === null ? [] : errors,
                isValid: errors !== null && !errors.length,
            });
        } else {
            didMountRef.current = true;
        }
    }, [file, data, errors]);

    return (
        <>
            <Root horizontal={horizontal} updatedDesign={updatedDesign}>
                {!updatedDesign && (
                    <CustomFileInput
                        type="file"
                        label={title || t('components.form.uploadButton')}
                        initialValue={file?.name}
                        onChange={handleChangeFile}
                        accept=".xlsx,.xls"
                    />
                )}

                {!file && linkExample && !linkExampleAction ? (
                    <CustomLink
                        updatedDesign={updatedDesign}
                        href={linkExample}
                        tabIndex={-1}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {updatedDesign && (
                            <Icon
                                source={DownloadIcon}
                                width={16}
                                height={16}
                                strokeColor={colors.brandPrimary}
                                fillColor={colors.white}
                                style={{marginRight: 8}}
                            />
                        )}
                        {t('components.xlsxForm.example')}
                    </CustomLink>
                ) : !file && linkExample && linkExampleAction ? (
                    <CustomLinkAction onClick={() => linkExampleAction(linkExample)} tabIndex={-1}>
                        {t('components.xlsxForm.example')}
                    </CustomLinkAction>
                ) : null}

                {updatedDesign && (
                    <CustomFileInput
                        updatedDesign
                        type="file"
                        label={title || t('components.form.uploadButton')}
                        initialValue={file?.name}
                        onChange={handleChangeFile}
                        accept=".xlsx,.xls"
                    />
                )}
            </Root>

            {serverValidate ? (
                <ErrorList>
                    {errorsList?.map(({error}, i) => (
                        <ErrorItem key={i}>{error}</ErrorItem>
                    ))}
                </ErrorList>
            ) : (
                <ErrorList>
                    {errors?.map((err, i) => (
                        <ErrorItem key={i}>{err}</ErrorItem>
                    ))}
                </ErrorList>
            )}
        </>
    );
};
